import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, OnInit } from '@angular/core';
import { Organization, OrgHat, OrgSecurityGroup, OrgUser, PendingPositionApproval } from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrgHattingService } from '../../services/org-hatting.service';
import { OrgDialogService } from '../../services/org-dialog.service';

export interface ApproveAssignmentDialogData {
    /**
     * Legacy/single-record flow — approve one PendingPositionApproval record.
     * Prefer `approvals` for bundled/multi-record review (e.g. several pending
     * requests for the same user on the same post).
     */
    approval?: PendingPositionApproval;
    /**
     * Bundle of approval records to review together. All records MUST share the
     * same `uid` and `positionKey`. The dialog merges their hats into a single
     * checkbox list so the admin can approve a subset in one pass; on submit
     * the dialog calls the approve/reject service once per underlying record
     * so the audit trail remains per-hat-source.
     */
    approvals?: PendingPositionApproval[];
    organization: Organization;
}

export interface HatSelectionItem {
    key: string;
    name: string;
    selected: boolean;
    isSpecialRights: boolean;
    securityGroupNames: string[];
    featureNames: string[];
    /** The approvalId this hat was originally requested in. */
    sourceApprovalId: string;
}

@Component({
    selector: 'shared-org-approve-assignment-dialog',
    imports: [MaterialModule, CommonModule, FormsModule],
    templateUrl: './org-approve-assignment-dialog.html',
    styleUrls: ['./org-approve-assignment-dialog.scss'],
})
export class OrgApproveAssignmentDialogComponent implements OnInit {
    /** All approval records this dialog is reviewing (1..N, same uid+positionKey). */
    public approvals: PendingPositionApproval[] = [];
    /** Convenience: the first approval, used for header/user/position info. */
    public approval!: PendingPositionApproval;
    public userName = '';
    public userAvatarUrl = '';
    public positionName = '';
    public requestedByName = '';
    public requestedByEmail = '';
    public requestedByDisplay = '';
    public hatItems: HatSelectionItem[] = [];
    public rejectionReason = '';
    public isProcessing = false;
    public showRejectConfirm = false;
    /** Surfaced when an approve/reject call fails — the dialog must never fail silently. */
    public errorMessage = '';

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ApproveAssignmentDialogData,
        private dialogRef: MatDialogRef<OrgApproveAssignmentDialogComponent>,
        private hattingService: OrgHattingService,
        private dialogService: OrgDialogService
    ) {
        // Normalize: accept either { approval } (legacy) or { approvals: [] }
        const bundle =
            Array.isArray(data.approvals) && data.approvals.length > 0
                ? data.approvals
                : data.approval
                  ? [data.approval]
                  : [];
        this.approvals = bundle;
        this.approval = bundle[0];
    }

    /**
     * Whether the user being approved is currently online (per the org-board
     * presence cache). Used to show a green dot on their avatar so the admin
     * has the same context here as on the board.
     */
    public get isUserOnline(): boolean {
        const uid = this.approval?.uid;
        if (!uid) return false;
        return (this.dialogService.onlineUserIds || []).includes(uid);
    }

    ngOnInit(): void {
        const org = this.data.organization;
        if (!this.approval) {
            // No approvals supplied — nothing to show, but keep dialog stable.
            return;
        }
        const uid = this.approval.uid;
        const requestedByUid = this.approval.requestedBy;

        // Resolve user name
        const orgUser = org.orgUsers?.[uid] as OrgUser | undefined;
        this.userName = orgUser?.name || orgUser?.userName || uid;

        // Resolve avatar
        this.userAvatarUrl = orgUser?.photoURL || '';

        // Position name
        this.positionName = this.approval.positionName || this.resolvePositionName(this.approval.positionKey, org);

        // Resolve requester identity (prefer name + email, never raw UID when resolvable)
        const requestedByUser = org.orgUsers?.[requestedByUid] as OrgUser | undefined;
        this.requestedByName = requestedByUser?.name || requestedByUser?.userName || '';
        this.requestedByEmail = requestedByUser?.email || '';
        if (this.requestedByName && this.requestedByEmail) {
            this.requestedByDisplay = `${this.requestedByName} (${this.requestedByEmail})`;
        } else if (this.requestedByName) {
            this.requestedByDisplay = this.requestedByName;
        } else if (this.requestedByEmail) {
            this.requestedByDisplay = this.requestedByEmail;
        } else {
            this.requestedByDisplay = requestedByUid || 'Unknown requester';
        }

        // Build hat selection items — de-dupe by hatKey across all bundled approvals.
        // If the same hat appears in multiple records, keep the first source and skip the rest.
        const seen = new Set<string>();
        const items: HatSelectionItem[] = [];
        for (const approval of this.approvals) {
            const specialSet = new Set<string>(approval.specialRightsHatKeys || []);
            const mainKeys = approval.hatKeys || [];
            const allKeys = [...mainKeys, ...specialSet].filter((k, i, arr) => arr.indexOf(k) === i);
            for (const hatKey of allKeys) {
                if (seen.has(hatKey)) continue;
                seen.add(hatKey);
                const hat = org.orgHats?.[hatKey] as OrgHat | undefined;
                const isSpecialRights = specialSet.has(hatKey);

                // Resolve security groups and features for preview
                const sgNames: string[] = [];
                const featureNames: string[] = [];
                if (hat) {
                    const sgRefs = hat.securityGroupsArray || [];
                    for (const sgRef of sgRefs) {
                        const sgKey = typeof sgRef === 'string' ? sgRef : (sgRef as OrgSecurityGroup)?.key;
                        if (!sgKey) continue;
                        const sg = org.orgSecurityGroups?.[sgKey] as OrgSecurityGroup | undefined;
                        if (sg) {
                            sgNames.push(sg.name || sgKey);
                            const features = sg.features || [];
                            for (const f of features) {
                                if (!featureNames.includes(f)) {
                                    featureNames.push(f);
                                }
                            }
                        }
                    }
                }

                items.push({
                    key: hatKey,
                    name: hat?.name || hatKey,
                    selected: true,
                    isSpecialRights,
                    securityGroupNames: sgNames,
                    featureNames: featureNames.sort(),
                    sourceApprovalId: approval.key,
                });
            }
        }
        this.hatItems = items;
    }

    get selectedHatCount(): number {
        return this.hatItems.filter((h) => h.selected).length;
    }

    get selectedHatKeys(): string[] {
        return this.hatItems.filter((h) => h.selected).map((h) => h.key);
    }

    get allFeatures(): string[] {
        const features = new Set<string>();
        for (const hat of this.hatItems) {
            if (hat.selected) {
                for (const f of hat.featureNames) {
                    features.add(f);
                }
            }
        }
        return Array.from(features).sort();
    }

    /**
     * Label for the primary approve button. The post placement is the fulcrum,
     * so we always show "Approve on post" — and clarify the hat count when > 0,
     * or hint that no hats will be assigned now when the admin unticked them all.
     */
    get approveButtonLabel(): string {
        const n = this.selectedHatCount;
        if (n === 0) return 'Approve on post (no hats)';
        if (n === 1) return 'Approve on post with 1 hat';
        return `Approve on post with ${n} hats`;
    }

    /**
     * Org key to act on for a given approval record. Records written by the
     * wizard staff stage / invitation acceptance historically lacked `orgId`
     * (the field is required on the interface but the Admin-SDK writers are
     * untyped) — fall back to the organization this dialog was opened for, or
     * the approve/reject call targets a nonexistent doc and no-ops.
     */
    private orgKeyFor(approval: PendingPositionApproval): string {
        return approval.orgId || this.data.organization?.key || '';
    }

    async onApprove(): Promise<void> {
        this.isProcessing = true;
        this.errorMessage = '';

        // Group selected hats by their source approval record so we can call
        // approvePositionAssignment once per record with the correct subset.
        const bySource = new Map<string, string[]>();
        for (const h of this.hatItems) {
            if (!h.selected) continue;
            const arr = bySource.get(h.sourceApprovalId) || [];
            arr.push(h.key);
            bySource.set(h.sourceApprovalId, arr);
        }

        let allSucceeded = true;
        const approvedHatKeys: string[] = [];

        // Resolve every approval record in the bundle. Records where every hat
        // was unticked still get APPROVED (with an empty hat set) — semantically
        // "admin reviewed and confirmed the user on the post but chose not to
        // assign these hats now." The deselected hats are removed from pending
        // by the same approvePositionAssignment call (see service: it removes
        // any originalHatKeys not in approvedHatKeys from pendingAssignedToArray).
        // The user can always be given those hats later via Manage Hats on Post.
        for (const approval of this.approvals) {
            const selectedForThis = bySource.get(approval.key) || [];
            const result = await this.hattingService.approvePositionAssignment(
                this.orgKeyFor(approval),
                approval.key,
                selectedForThis
            );
            if (!result.succeeded) {
                allSucceeded = false;
                this.errorMessage = result.error || 'Failed to approve assignment.';
            } else approvedHatKeys.push(...selectedForThis);
        }

        this.isProcessing = false;
        if (allSucceeded) {
            this.dialogRef.close({ action: 'approved', approvedHatKeys });
        }
    }

    async onReject(): Promise<void> {
        if (!this.showRejectConfirm) {
            this.showRejectConfirm = true;
            return;
        }

        this.isProcessing = true;
        this.errorMessage = '';
        let allSucceeded = true;
        for (const approval of this.approvals) {
            const result = await this.hattingService.rejectPositionAssignment(
                this.orgKeyFor(approval),
                approval.key,
                this.rejectionReason || undefined
            );
            if (!result.succeeded) {
                allSucceeded = false;
                this.errorMessage = result.error || 'Failed to reject assignment.';
            }
        }

        this.isProcessing = false;
        if (allSucceeded) {
            this.dialogRef.close({ action: 'rejected' });
        }
    }

    onCancelReject(): void {
        this.showRejectConfirm = false;
        this.rejectionReason = '';
    }

    private resolvePositionName(positionKey: string, org: Organization): string {
        if (!positionKey) return 'Special Rights Assignment';
        const part = org.orgParts?.find(
            (p: { partKey?: string; key?: string; name?: string }) => p.partKey === positionKey || p.key === positionKey
        );
        return part?.name || positionKey;
    }
}
